import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabaseClient';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { LoadingSpinner } from '../../components/ui/LoadingState';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { usePreferences } from '../../hooks/usePreferences';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import { logActivity } from '../../utils/activityLogger';
import { cn } from '../../utils/cn';
import WriteOffModal from '../../components/WriteOffModal';
import WriteOffReviewPanel from './components/WriteOffReviewPanel';
import { useSelector } from 'react-redux';
import { exportToProfessionalCSV } from '../../utils/csvExport';
import { calculateEOLDate } from '../../utils/assetUtils';

const WriteOffsPage = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('pending');
    const [isLoading, setIsLoading] = useState(true);
    const [disposedAssets, setDisposedAssets] = useState([]);
    const [pendingAssets, setPendingAssets] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [reasonFilter, setReasonFilter] = useState('');
    const [methodFilter, setMethodFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedAssetIds, setSelectedAssetIds] = useState([]);
    const [isBulkApproving, setIsBulkApproving] = useState(false);

    // Modal & Panel state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [isReviewPanelOpen, setIsReviewPanelOpen] = useState(false);
    const [reviewAsset, setReviewAsset] = useState(null);
    const [selectedDisposed, setSelectedDisposed] = useState(null);

    const { user: authUser } = useSelector((state) => state.auth);
    const userId = authUser?.id;
    const { preferences } = usePreferences();
    const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

    const addNotification = (message, type = 'info') => {
        setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
    };

    useEffect(() => {
        fetchData();
        setSelectedAssetIds([]);
    }, [activeTab]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            if (activeTab === 'disposed') {
                const { data, error } = await supabase
                    .from('assets')
                    .select(`
                        *,
                        activities!activities_asset_tag_fkey (
                            type,
                            created_at,
                            details
                        )
                    `)
                    .or('status.eq.Written Off,is_archived.eq.true')
                    .order('created_at', { ascending: false });

                if (error) throw error;

                const mappedAssets = data.map(asset => {
                    const writeOffActivity = asset.activities?.find(act => act.type === 'asset_written_off');
                    return {
                        ...asset,
                        write_off_date: writeOffActivity?.created_at || asset.updated_at,
                        reason: writeOffActivity?.details?.reason || 'N/A',
                        disposal_method: writeOffActivity?.details?.disposal_method || 'N/A',
                        justification: writeOffActivity?.details?.justification || 'N/A',
                        disposed_by: writeOffActivity?.details?.disposed_by || writeOffActivity?.details?.requested_by || 'N/A',
                        disposed_by_contact: writeOffActivity?.details?.disposed_by_contact || writeOffActivity?.details?.requested_by_contact || 'N/A',
                        authorized_by: writeOffActivity?.details?.authorized_by || 'N/A'
                    };
                });
                setDisposedAssets(mappedAssets);
            } else {
                const { data, error } = await supabase
                    .from('assets')
                    .select('*')
                    .eq('is_archived', false)
                    .in('status', ['Pending Write-Off', 'Pending write-off', 'pending write-off', 'Broken', 'broken', 'Available', 'In Use', 'In Repair'])
                    .order('updated_at', { ascending: false });

                if (error) throw error;

                const today = new Date();
                const writeOffQueue = (data || []).filter(asset => {
                    // 1. Manually marked or broken assets always stay
                    const isManual = ['Pending Write-Off', 'Pending write-off', 'pending write-off', 'Broken', 'broken'].includes(asset.status);
                    if (isManual) return true;

                    // 2. Active assets only stay if they reached EOL
                    if (asset.purchase_date && asset.lifespan_years) {
                        const eolDate = calculateEOLDate(asset.purchase_date, asset.lifespan_years);
                        return eolDate && eolDate < today;
                    }

                    return false;
                }).map(asset => ({
                    ...asset,
                    isAutoAdded: !['Pending Write-Off', 'Pending write-off', 'pending write-off', 'Broken', 'broken'].includes(asset.status)
                }));

                setPendingAssets(writeOffQueue);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            addNotification(t('write_offs.load_error', { message: error.message }), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleProcessDisposal = (asset) => {
        setSelectedAsset(asset);
        setIsModalOpen(true);
    };

    const handleReviewAsset = (asset) => {
        setReviewAsset(asset);
        setIsReviewPanelOpen(true);
    };

    const handleSuccess = (message) => {
        addNotification(message, 'success');
        fetchData();
    };

    const filteredDisposed = useMemo(() => {
        const filtered = disposedAssets.filter(asset => {
            const matchesSearch = asset.asset_tag.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                 asset.product_name.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesReason = !reasonFilter || asset.reason === reasonFilter;
            const matchesMethod = !methodFilter || asset.disposal_method === methodFilter;
            
            // Date Range Filtering
            let matchesDateRange = true;
            if (startDate || endDate) {
                const assetDate = new Date(asset.write_off_date);
                if (startDate) {
                    matchesDateRange = matchesDateRange && assetDate >= new Date(startDate);
                }
                if (endDate) {
                    // Set end date to the end of the day
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    matchesDateRange = matchesDateRange && assetDate <= end;
                }
            }

            return matchesSearch && matchesReason && matchesMethod && matchesDateRange;
        });

        return filtered.sort((a, b) => {
            const dateA = new Date(a.write_off_date).getTime();
            const dateB = new Date(b.write_off_date).getTime();
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
    }, [disposedAssets, searchQuery, reasonFilter, methodFilter, sortOrder, startDate, endDate]);

    const filteredPending = useMemo(() => {
        return pendingAssets.filter(asset => {
            const matchesSearch = asset.asset_tag.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                 asset.product_name.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = !categoryFilter || asset.category === categoryFilter;
            return matchesSearch && matchesCategory;
        });
    }, [pendingAssets, searchQuery, categoryFilter]);

    // Selection Handlers
    const toggleSelectAll = () => {
        if (selectedAssetIds.length === filteredPending.length) {
            setSelectedAssetIds([]);
        } else {
            setSelectedAssetIds(filteredPending.map(a => a.asset_tag));
        }
    };

    const toggleSelectRow = (assetTag) => {
        setSelectedAssetIds(prev => 
            prev.includes(assetTag) 
                ? prev.filter(id => id !== assetTag) 
                : [...prev, assetTag]
        );
    };

    const handleBulkApprove = async () => {
        if (selectedAssetIds.length === 0) return;
        
        setIsBulkApproving(true);
        try {
            // 1. Bulk Update Status
            const { error: updateError } = await supabase
                .from('assets')
                .update({ 
                    status: 'Written Off', 
                    is_archived: true 
                })
                .in('asset_tag', selectedAssetIds);

            if (updateError) throw updateError;

            // 2. Log Activity for each (in parallel)
            await Promise.all(selectedAssetIds.map(tag => 
                logActivity(
                    'asset_written_off',
                    t('write_offs.bulk_approved_activity'),
                    tag,
                    userId,
                    {
                        reason: 'Bulk Approval',
                        disposal_method: 'Pending Batch Disposal',
                        justification: 'Approved via bulk action in IT Management dashboard',
                        requested_by: authUser?.email || userId,
                        requested_by_contact: authUser?.email || 'N/A',
                        authorized_by: authUser?.email || userId
                    }
                )
            ));

            addNotification(t('write_offs.bulk_approved_success', { count: selectedAssetIds.length }), 'success');
            setSelectedAssetIds([]);
            fetchData();
        } catch (error) {
            console.error('Bulk Approval Error:', error);
            addNotification(t('write_offs.bulk_approval_failed', { message: error.message }), 'error');
        } finally {
            setIsBulkApproving(false);
        }
    };

    const selectionSummary = useMemo(() => {
        const selected = pendingAssets.filter(a => selectedAssetIds.includes(a.asset_tag));
        return {
            count: selected.length,
            totalValue: selected.reduce((sum, a) => sum + (Number(a.purchase_price) || 0), 0)
        };
    }, [pendingAssets, selectedAssetIds]);

    const disposedMetrics = useMemo(() => {
        return {
            count: filteredDisposed.length,
            totalValue: filteredDisposed.reduce((sum, a) => sum + (Number(a.purchase_price) || 0), 0)
        };
    }, [filteredDisposed]);

    // KPI Calculations
    const pendingCount = pendingAssets.length;

    // Filter Options
    const reasonOptions = Array.from(new Set(disposedAssets.map(a => a.reason)))
        .filter(r => r !== 'N/A')
        .map(r => ({ value: r, label: r }));
    
    const methodOptions = Array.from(new Set(disposedAssets.map(a => a.disposal_method)))
        .filter(m => m !== 'N/A')
        .map(m => ({ value: m, label: m }));

    const categoryOptions = useMemo(() => {
        const categories = Array.from(new Set(pendingAssets.map(a => a.category))).filter(Boolean);
        return categories.map(c => ({ value: c, label: c }));
    }, [pendingAssets]);

    const exportToCSV = () => {
        const dataToExport = activeTab === 'disposed' ? filteredDisposed : filteredPending;
        if (dataToExport.length === 0) {
            addNotification('No data to export', 'warning');
            return;
        }

        const columns = activeTab === 'disposed'
                ? [
                { key: 'asset_tag', label: 'Asset Tag' },
                { key: 'product_name', label: 'Product Name' },
                { key: 'write_off_date', label: 'Write-Off Date', value: (asset) => formatDate(asset.write_off_date, preferredFormat) },
                { key: 'reason', label: 'Reason' },
                { key: 'disposal_method', label: 'Disposal Method' },
                { key: 'purchase_price', label: 'Original Price' },
                { key: 'justification', label: 'Justification' },
            ]
            : [
                { key: 'asset_tag', label: 'Asset Tag' },
                { key: 'product_name', label: 'Product Name' },
                { key: 'status', label: 'Status' },
                { key: 'purchase_price', label: 'Purchase Price' },
                { key: 'purchase_date', label: 'Purchase Date' },
            ];

        exportToProfessionalCSV({
            data: dataToExport,
            reportTitle: activeTab === 'disposed' ? 'Write-Off Disposals Report' : 'Write-Off Queue Report',
            appliedFilters: {
                Tab: activeTab,
                Search: searchQuery || 'None',
                Reason: reasonFilter || 'All Reasons',
                Method: methodFilter || 'All Methods',
                Category: categoryFilter || 'All Categories',
                DateRange: startDate || endDate ? `${startDate || 'Any'} to ${endDate || 'Any'}` : 'Any',
            },
            fileNamePrefix: `Asset_${activeTab}_Export`,
            columns,
        });
        addNotification('Export successful', 'success');
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">{t('write_offs.page_title')}</h1>
                    <p className="text-muted-foreground">{t('write_offs.page_subtitle')}</p>
                </div>
                <div className="flex gap-3">
                    {activeTab === 'pending' && selectedAssetIds.length > 0 && (
                        <div className="flex items-center gap-4 px-4 py-1.5 bg-muted/50 border border-border rounded-lg animate-in fade-in slide-in-from-right-4 duration-200">
                            <div className="flex flex-col items-end">
                                <span className="text-sm font-bold text-foreground">
                                    {t('write_offs.assets_selected', { count: selectionSummary.count })}
                                </span>
                                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-tight">
                                    {t('write_offs.total_value')}: {formatCurrency(selectionSummary.totalValue)}
                                </span>
                            </div>
                            <div className="w-px h-8 bg-border" />
                            <Button 
                                variant="primary" 
                                className="bg-success hover:bg-success/90 border-none shadow-lg shadow-success/20"
                                iconName="CheckCircle"
                                onClick={handleBulkApprove}
                                loading={isBulkApproving}
                            >
                                {t('write_offs.approve_for_disposal', { count: selectionSummary.count })}
                            </Button>
                        </div>
                    )}
                    <Button variant="outline" iconName="Download" onClick={exportToCSV}>{t(activeTab === 'pending' ? 'write_offs.export_queue_csv' : 'write_offs.export_audit_log_csv')}</Button>
                </div>
            </div>

            {/* Tab Buttons */}
            <div className="flex space-x-2 border-b border-border">
                <button
                    onClick={() => setActiveTab('pending')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'pending' 
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    {t('write_offs.pending_tab')}
                    {pendingCount > 0 && (
                        <span className="ml-2 bg-warning/20 text-warning px-2 py-0.5 rounded-full text-xs font-bold">
                            {pendingCount}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('disposed')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'disposed' 
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    {t('write_offs.disposed_tab')}
                </button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg text-primary">
                        <Icon name="Clock" size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">{t('write_offs.pending_writeoffs')}</p>
                        <h2 className="text-2xl font-bold">{pendingCount}</h2>
                    </div>
                </div>
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-success/10 rounded-lg text-success">
                        <Icon name="Trash2" size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                            {activeTab === 'disposed' ? t('write_offs.visible_disposals') : t('write_offs.total_disposals')}
                        </p>
                        <h2 className="text-2xl font-bold">
                            {activeTab === 'disposed' ? disposedMetrics.count : disposedAssets.length}
                        </h2>
                    </div>
                </div>
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-warning/10 rounded-lg text-warning">
                        <Icon name="DollarSign" size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                            {activeTab === 'disposed' ? t('write_offs.visible_value') : t('write_offs.total_value_disposed')}
                        </p>
                        <h2 className="text-2xl font-bold">
                            {activeTab === 'disposed' ? formatCurrency(disposedMetrics.totalValue) : formatCurrency(disposedAssets.reduce((sum, a) => sum + (Number(a.purchase_price) || 0), 0))}
                        </h2>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-card p-4 rounded-xl border border-border shadow-sm flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 w-full">
                        <Input
                        label={t('write_offs.search_assets')}
                        placeholder={t('write_offs.search_placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        iconName="Search"
                    />
                </div>
                <div className="w-full md:w-48">
                    {activeTab === 'pending' ? (
                            <Select
                                label={t('write_offs.category')}
                            options={categoryOptions}
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                                placeholder={t('write_offs.all_categories')}
                            clearable
                        />
                    ) : (
                            <Select
                                label={t('write_offs.reason')}
                            options={reasonOptions}
                            value={reasonFilter}
                            onChange={setReasonFilter}
                                placeholder={t('write_offs.all_reasons')}
                            clearable
                        />
                    )}
                </div>
                {activeTab === 'disposed' && (
                    <>
                        <div className="w-full md:w-48">
                            <Select
                                label={t('write_offs.disposal_method')}
                                options={methodOptions}
                                value={methodFilter}
                                onChange={setMethodFilter}
                                placeholder={t('write_offs.all_methods')}
                                clearable
                            />
                        </div>
                        <div className="flex flex-row gap-4 w-full md:w-auto flex-1 items-center">
                            <div className="flex flex-row items-center gap-2 flex-1 min-w-0">
                                <label className="text-[10px] font-bold text-muted-foreground/70 uppercase whitespace-nowrap">{t('write_offs.from')}</label>
                                <div className="flex-1 min-w-0">
                                    <Input
                                        type="date"
                                        className="w-full min-w-0"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-row items-center gap-2 flex-1 min-w-0">
                                <label className="text-[10px] font-bold text-muted-foreground/70 uppercase whitespace-nowrap">{t('write_offs.to')}</label>
                                <div className="flex-1 min-w-0">
                                    <Input
                                        type="date"
                                        className="w-full min-w-0"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        {(startDate || endDate) && (
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-10 mb-0.5 text-muted-foreground hover:text-foreground"
                                iconName="X"
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                }}
                            >
                                {t('write_offs.clear')}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Content Tables */}
            <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden min-h-[400px]">
                {isLoading ? (
                    <div className="flex items-center justify-center h-[400px]">
                        <LoadingSpinner size="lg" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        {activeTab === 'pending' ? (
                            <table className="min-w-full divide-y divide-border/60 dark:divide-border/35">
                                <thead className="bg-muted">
                                    <tr>
                                        <th scope="col" className="px-6 py-4 w-12 text-left">
                                            <Checkbox 
                                                checked={filteredPending.length > 0 && selectedAssetIds.length === filteredPending.length}
                                                indeterminate={selectedAssetIds.length > 0 && selectedAssetIds.length < filteredPending.length}
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.asset_tag')}</th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.product_name')}</th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.category')}</th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.status')}</th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.purchase_price')}</th>
                                        <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-card divide-y divide-border/50 dark:divide-border/30">
                                    {filteredPending.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-12 text-center text-muted-foreground/70">
                                                <Icon name="Inbox" size={48} className="mx-auto mb-4 opacity-20" />
                                                <p className="text-sm font-medium">{t('write_offs.no_pending_assets')}</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredPending.map((asset) => (
                                            <tr 
                                                key={asset.asset_tag} 
                                                className={cn(
                                                    "hover:bg-muted/80 transition-colors cursor-pointer group",
                                                    (selectedAssetIds.includes(asset.asset_tag) || reviewAsset?.asset_tag === asset.asset_tag) && "bg-primary/10"
                                                )}
                                                onClick={() => handleReviewAsset(asset)}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleSelectRow(asset.asset_tag);
                                                }}>
                                                    <Checkbox 
                                                        checked={selectedAssetIds.includes(asset.asset_tag)}
                                                        onChange={() => toggleSelectRow(asset.asset_tag)}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-primary group-hover:underline">
                                                        {asset.asset_tag}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-foreground">{asset.product_name}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                    {asset.category}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                                                            String(asset.status || '').toLowerCase() === 'broken' 
                                                            ? 'bg-destructive/10 text-destructive border-destructive/20' 
                                                            : 'bg-warning/10 text-warning border-warning/20'
                                                        }`}>
                                                            {asset.status || t('write_offs.unknown')}
                                                        </span>
                                                        {asset.isAutoAdded && (
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-destructive/10 text-destructive border border-destructive/20 uppercase tracking-tighter">
                                                                {t('write_offs.auto_added_eol')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap font-bold text-foreground">
                                                    {formatCurrency(asset.purchase_price)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost" 
                                                        className="text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20 font-bold text-xs"
                                                        onClick={() => handleProcessDisposal(asset)}
                                                    >
                                                        {t('write_offs.process_disposal')}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        ) : (
                            <table className="min-w-full divide-y divide-border/60 dark:divide-border/35">
                                <thead className="bg-muted">
                                    <tr>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.asset_tag')}</th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.product_name')}</th>
                                        <th 
                                            scope="col"
                                            className={cn(
                                                "px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none group",
                                                "flex items-center gap-1"
                                            )}
                                            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                        >
                                            {t('write_offs.write_off_date')}
                                            <div className="text-muted-foreground/60 group-hover:text-muted-foreground transition-opacity">
                                                {sortOrder === 'desc' ? (
                                                    <Icon name="ChevronDown" size={14} />
                                                ) : (
                                                    <Icon name="ChevronUp" size={14} />
                                                )}
                                            </div>
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.reason')}</th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.method')}</th>
                                        <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('write_offs.original_value')}</th>
                                        <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">Details</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-card divide-y divide-border/50 dark:divide-border/30">
                                    {filteredDisposed.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-12 text-center text-muted-foreground/70">
                                                <Icon name="Inbox" size={48} className="mx-auto mb-4 opacity-20" />
                                                <p className="text-sm font-medium">
                                                    {(startDate || endDate) 
                                                        ? t('write_offs.no_disposed_in_range') 
                                                        : t('write_offs.no_disposal_records')}
                                                </p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredDisposed.map((asset) => (
                                            <tr key={asset.asset_tag} className={cn("hover:bg-muted/80 transition-colors", selectedDisposed?.asset_tag === asset.asset_tag && "bg-primary/5")}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-primary">
                                                        {asset.asset_tag}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-foreground">{asset.product_name}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                    {formatDate(asset.write_off_date, preferredFormat)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-muted/50 text-foreground/80 border border-border">
                                                        {asset.reason}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                    {asset.disposal_method}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-foreground">
                                                    {formatCurrency(asset.purchase_price)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        iconName="FileText"
                                                        className="text-primary hover:bg-primary/10 text-xs font-bold"
                                                        onClick={() => setSelectedDisposed(selectedDisposed?.asset_tag === asset.asset_tag ? null : asset)}
                                                    >
                                                        {selectedDisposed?.asset_tag === asset.asset_tag ? 'Close' : 'Details'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>

            {isModalOpen && selectedAsset && (
                <WriteOffModal
                    asset={selectedAsset}
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setSelectedAsset(null);
                    }}
                    onSuccess={handleSuccess}
                />
            )}

            {/* Disposed Asset Detail Panel */}
            <AnimatePresence>
                {selectedDisposed && (
                    <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 24 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        className="bg-card border border-border rounded-xl shadow-lg overflow-hidden animate-in fade-in duration-200"
                    >
                        {/* Panel Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/40">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-error/10 rounded-lg">
                                    <Icon name="FileText" size={18} className="text-error" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-foreground">Disposal Audit Record</h4>
                                    <p className="text-xs text-muted-foreground">{selectedDisposed.asset_tag} — {selectedDisposed.product_name}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedDisposed(null)} className="text-muted-foreground hover:text-foreground transition-colors p-1.5 hover:bg-muted rounded-full">
                                <Icon name="X" size={16} />
                            </button>
                        </div>

                        {/* Panel Body */}
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left column */}
                            <div className="space-y-4">
                                <div>
                                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Write-Off Date</span>
                                    <p className="text-sm font-semibold text-foreground mt-0.5">{formatDate(selectedDisposed.write_off_date, preferredFormat)}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Reason</span>
                                    <p className="text-sm font-semibold text-foreground mt-0.5">
                                        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-muted/50 text-foreground/80 border border-border">
                                            {selectedDisposed.reason}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Disposal Method</span>
                                    <p className="text-sm font-semibold text-foreground mt-0.5">{selectedDisposed.disposal_method}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Original Purchase Price</span>
                                    <p className="text-sm font-bold text-foreground mt-0.5">{formatCurrency(selectedDisposed.purchase_price)}</p>
                                </div>
                            </div>

                            {/* Right column */}
                            <div className="space-y-4">
                                <div>
                                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Disposed By / Company Name</span>
                                    <p className="text-sm font-semibold text-foreground mt-0.5 flex items-center gap-1.5">
                                        <Icon name="Truck" size={13} className="text-muted-foreground" />
                                        {selectedDisposed.disposed_by}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Contractor Contact</span>
                                    <p className="text-sm font-semibold text-foreground mt-0.5 flex items-center gap-1.5">
                                        <Icon name="Mail" size={13} className="text-muted-foreground" />
                                        {selectedDisposed.disposed_by_contact}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Authorized By (System)</span>
                                    <p className="text-sm text-muted-foreground mt-0.5">{selectedDisposed.authorized_by}</p>
                                </div>
                            </div>

                            {/* Justification full-width */}
                            <div className="md:col-span-2">
                                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Justification / Audit Notes</span>
                                <div className="mt-2 p-4 bg-muted/50 rounded-lg border border-border text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                                    {selectedDisposed.justification !== 'N/A' ? selectedDisposed.justification : <span className="text-muted-foreground italic">No justification recorded.</span>}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <WriteOffReviewPanel
                asset={reviewAsset}
                isOpen={isReviewPanelOpen}
                onClose={() => {
                    setIsReviewPanelOpen(false);
                    setReviewAsset(null);
                }}
                onApprove={(asset) => {
                    setIsReviewPanelOpen(false);
                    setReviewAsset(null);
                    handleProcessDisposal(asset);
                }}
                onSuccess={handleSuccess}
            />

            <NotificationContainer 
                notifications={notifications} 
                onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} 
            />
        </div>
    );
};

export default WriteOffsPage;
